import { inject, Injectable } from '@angular/core';
import { UploadService } from '../services/upload.service';
import { catchError, concatMap, EMPTY, map, Subject, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {FileData, FileDataUpload, WorkflowType} from '@shared-types/shared-file';
import {
  UploadedFileInformation,
  UploadResponse
} from '@shared-types/shared-upload';
import {WorkflowService} from "@shared-api/workflow/workflow.service";

interface SignedUrlResponse {
  file: FileDataUpload;
  uploadResponse: UploadResponse;
}

@Injectable({
  providedIn: 'root'
})
export class UploadComponentService {
  private uploadService = inject(UploadService);

  // source
  processes = new Subject<UploadedFileInformation>();

  // Actions
  private error$ = new Subject<unknown>();

  uploadFile$ = new Subject<FileDataUpload>();
  private uploading$ = this.uploadFile$.pipe(
    concatMap((file: FileDataUpload) => {
      return this.getSignedUrl(file).pipe(
        map(
          (uploadResponse) => ({ file, uploadResponse }) as SignedUrlResponse
        ),
        catchError((err) => {
          console.error(err);
          this.error$.next(err);
          return EMPTY;
        })
      );
    })
  );

  deleteFile$ = new Subject<{
    file: FileData;
    workflowId: string;
    workflowType: WorkflowType;
  }>();

  // Helper actions
  private signedUrlUpload$ = new Subject<SignedUrlResponse>();
  private signedUrl$ = this.signedUrlUpload$.pipe(
    concatMap((response: SignedUrlResponse) => {
      return this.uploadFileWithSignedUrl(
        response.file.file,
        response.uploadResponse.signedUrl.url
      ).pipe(
        map((signedUrlResponse) => ({ response, signedUrlResponse })),
        catchError((err) => {
          console.error(err);
          this.error$.next(err);
          return EMPTY;
        })
      );
    })
  );

  constructor(private workflowService: WorkflowService) {
    this.uploading$.pipe(takeUntilDestroyed()).subscribe({
      next: (response: SignedUrlResponse) => {
        this.processes.next({
          process: response.uploadResponse.process,
          displayName: response.file.file.name,
          storedFilename: response.uploadResponse.signedUrl.filename
        });
        this.signedUrlUpload$.next(response);
      }
    });

    this.deleteFile$.pipe(takeUntilDestroyed()).subscribe({
      next: (data) => {
        this.deleteFile(data.file, data.workflowId);
      }
    });

    this.signedUrl$.pipe(takeUntilDestroyed()).subscribe();
  }

  private getSignedUrl(file: FileDataUpload) {
    return this.uploadService.getSignedUrl(file);
  }

  private putFile(filename?: string) {
    return this.uploadService.updateFiles(filename);
  }

  private uploadFileWithSignedUrl(file: File, signedUrl: string) {
    return this.uploadService.uploadToSignedUrl(file, signedUrl);
  }

  private deleteFile(fileData: FileData, workflowId: string) {
    if (fileData.storedName) {
      this.uploadService
        .deleteFile(workflowId, fileData.storedName)
        .subscribe();
    }
  }

  
}
